import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { CheckPlatformService } from '@services/helpers/check-platform.service';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { MfaOtpPromptService } from '@services/helpers/mfa-otp-prompt.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { Utils } from '@services/helpers/utils';
import { ApiService } from '@services/api/api.service';
import { TokenService } from '@services/helpers/token.service';
import { ActivityTrackerService } from '@services/user-activity/activity-tracker.service';
import { isChunkLoadError, recoverFromStaleChunk, unregisterDevServerServiceWorker, RELOAD_GUARD_KEY } from '@services/helpers/stale-chunk-recovery';
import { buildInfo } from './core/environments/build-info';

const $: any = require('jquery');

interface AppBuildInfo {
  BUILD_ID?: string;
  GIT_COMMIT_HASH?: string;
  BUILD_DATE?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  Utils = Utils; // 👈 expose Utils class to the template
  title = 'remmi-app';
  public inset2dark = { axis: 'y' };
  isBrowser: boolean = this.checkPlatformService.isBrowser();
  /** Minimum gap between build-info fetches; collapses focus + visibility triggers into one call. */
  private readonly minBuildCheckGapInMs = 5 * 60 * 1000;
  private lastBuildCheckAt = 0;
  private readonly currentBuildId = this.getBuildFingerprint(buildInfo);
  private readonly handleWindowFocus = () => this.checkForAppUpdate();
  private readonly handleVisibilityChange = () => {
    if (!document.hidden) {
      this.checkForAppUpdate();
    }
  };
  private appUpdateCheckSubscription: Subscription;
  isUpdatePromptVisible = false;
  private latestAvailableBuildId = '';
  private dismissedBuildId = '';
  constructor(
    private router: Router,
    private lmService: LeadManagementService,
    private api: ApiService,
    private localStorageService: LocalStorageService,
    private checkPlatformService: CheckPlatformService,
    private activatedRoute: ActivatedRoute,
    private swUpdate: SwUpdate,
    private tokenService: TokenService,
    private activityTracker: ActivityTrackerService,
    readonly mfaOtpPrompt: MfaOtpPromptService
  ) {
    this.handleServiceWorkerUpdates();
    this.startAppUpdateChecks();
    router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        switchMap(route => route.data)
      )
      .subscribe((event: NavigationEnd | any) => {
        const currentURL = this.router.url;
        if (currentURL == '/setting/workflow') {
          this.checkPlatformService.isBrowser() && document.body.classList.add('overflow-hidden');
        } else {
          this.checkPlatformService.isBrowser() && document.body.classList.remove('overflow-hidden');
        }
        this.lmService.currentURL(currentURL);
        this.activityTracker.setPage(currentURL, event?.title ?? null);
        this.formatPinPage(currentURL, event?.breadcrumb?.slice(-1)?.[0]?.label || '');
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.toggleLoader('show');
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.toggleLoader('hide');
      }
      if (event instanceof NavigationError) {
        this.recoverIfStaleLazyChunk(event.error);
      }
    });
  }

  ngOnInit() {
    // Clear any service worker a previous `ng serve --configuration dev-deploy|staging|live`
    // installed on localhost. Registration is now disabled there, but an already-installed
    // worker keeps intercepting lazy-chunk requests — hanging navigation with no error —
    // until the page reloads without it. No-op off localhost and when nothing is registered.
    if (this.checkPlatformService.isBrowser()) {
      unregisterDevServerServiceWorker();
    }
    this.formatPinPage(this.router.url);
    this.clearChunkReloadGuardWhenStable();
  }

  /**
   * The global ErrorHandler reloads once on a stale lazy-chunk failure and sets
   * a sessionStorage guard to prevent a reload loop. Clear that guard only after
   * the app has run stably for a while, so a genuinely recovered session is ready
   * for the next deployment, while a crash-loop (which never survives this long)
   * keeps the guard set and stays broken.
   */
  private clearChunkReloadGuardWhenStable(): void {
    if (!this.checkPlatformService.isBrowser()) return;
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
      } catch {
        /* sessionStorage unavailable — nothing to clear */
      }
    }, 10000);
  }

  /**
   * A failed lazy-route `loadChildren` import surfaces as a router NavigationError
   * (it does not always reach the global ErrorHandler). After a deployment the
   * hashed chunk a stale client requests no longer exists, so navigating to a
   * lazy route (e.g. /setting/setup/...) throws and the SPA hangs. Recover via the
   * shared handler: purge the stale service-worker cache + unregister the worker,
   * then reload for the fresh build. A plain reload alone re-serves the same stale
   * cache and reload-loops. The shared sessionStorage guard prevents that loop.
   */
  private recoverIfStaleLazyChunk(error: any): void {
    if (!this.checkPlatformService.isBrowser()) return;
    if (!isChunkLoadError(error)) return;
    recoverFromStaleChunk();
  }

  ngOnDestroy(): void {
    this.appUpdateCheckSubscription?.unsubscribe();
    window.removeEventListener('focus', this.handleWindowFocus);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /** Same labels as login MFA step (`LoginComponent.otpType`). */
  mfaOtpType(): string {
    const typeMap: Record<string, string> = {
      'email-authenticator': 'Email',
      'google-authenticator': 'Google Authenticator App',
      'microsoft-authenticator': 'Microsoft Authenticator App',
      'authy-authenticator': 'Authy Authenticator App',
    };
    const t = this.mfaOtpPrompt.authentication_type;
    return t ? typeMap[t] || '' : '';
  }

  canSubmitSensitiveMfa(): boolean {
    const code = (this.mfaOtpPrompt.otpInput || '').replace(/\D/g, '');
    return code.length === 6;
  }

  /** Logged-in user shown on the MFA dialog so it's clear whose code is required. */
  mfaLoggedInUserName(): string {
    return this.api.getUserName() || '';
  }

  onMfaSensitiveSubmit(): void {
    if (!this.canSubmitSensitiveMfa()) {
      return;
    }
    this.mfaOtpPrompt.submitFromDialog();
  }

  /**
   * Prompts users when a deployed ngsw-worker build is ready, and recovers
   * automatically if the runtime worker is unrecoverable.
   */
  private handleServiceWorkerUpdates(): void {
    if (!this.checkPlatformService.isBrowser() || !this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe(() => {
      this.showUpdateAvailablePrompt();
    });

    this.swUpdate.unrecoverable.subscribe(() => {
      document.location.reload();
    });
  }

  private startAppUpdateChecks(): void {
    if (!this.checkPlatformService.isBrowser()) return;

    this.checkForAppUpdate();
    // No background polling — only check when the user actually returns to the tab/window.
    window.addEventListener('focus', this.handleWindowFocus);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private checkForAppUpdate(): void {
    this.checkForServiceWorkerUpdate();
    this.checkLatestBuildInfo();
  }

  private checkForServiceWorkerUpdate(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.checkForUpdate().catch(() => {
      /* ignore update check errors */
    });
  }

  private checkLatestBuildInfo(): void {
    const now = Date.now();
    if (now - this.lastBuildCheckAt < this.minBuildCheckGapInMs) return;
    this.lastBuildCheckAt = now;

    const buildInfoUrl = new URL('build-info.json', document.baseURI);
    buildInfoUrl.searchParams.set('ngsw-bypass', Date.now().toString());

    fetch(buildInfoUrl.toString(), { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .then((latestBuildInfo: AppBuildInfo | null) => {
        const latestBuildId = this.getBuildFingerprint(latestBuildInfo);
        if (latestBuildId && latestBuildId !== this.currentBuildId) {
          this.showUpdateAvailablePrompt(latestBuildId);
        }
      })
      .catch(() => {
        /* ignore build info check errors */
      });
  }

  private getBuildFingerprint(info: AppBuildInfo | null | undefined): string {
    if (!info) return '';

    return info.BUILD_ID || `${info.GIT_COMMIT_HASH || ''}-${info.BUILD_DATE || ''}`;
  }

  isUserLoggedIn(): boolean {
    return this.tokenService.isUserloggedIn();
  }

  /** True only on a deployed (live) domain, so the update prompt never appears during local development. */
  private isLiveDomain(): boolean {
    if (!this.checkPlatformService.isBrowser()) return false;

    const host = window.location.hostname;
    return host !== 'localhost' && host !== '127.0.0.1' && host !== '[::1]' && !host.endsWith('.local');
  }

  private showUpdateAvailablePrompt(latestBuildId = ''): void {
    if (!this.isLiveDomain() || !this.isUserLoggedIn()) return;

    if (latestBuildId) {
      this.latestAvailableBuildId = latestBuildId;
    }

    if (this.isUpdatePromptVisible || (this.latestAvailableBuildId && this.latestAvailableBuildId === this.dismissedBuildId)) return;

    this.isUpdatePromptVisible = true;
  }

  refreshApplication(): void {
    const data = {
      user_id: this.api.getUserId(),
      reloaded: true,
    };

    this.api.post('/force-to-reload', data).subscribe(async () => {
      try {
        // 1. Clear Cache Storage (Cache API — used by service workers / PWAs)
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }

        // 2. Unregister service workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
        }

        // 3. (Optional) clear app storage if you want a truly fresh state
        // localStorage.clear();
        // sessionStorage.clear();
      } catch (e) {
        console.warn('Cache clearing failed:', e);
      }

      // 4. Hard reload with cache-busting query param
      window.location.replace(window.location.pathname + '?nocache=' + Date.now());
    });
  }

  dismissUpdatePrompt(): void {
    this.dismissedBuildId = this.latestAvailableBuildId;
    this.isUpdatePromptVisible = false;
  }

  formatPinPage(url, pagetitle: string = '') {
    let element = $('.custom-bread-crumb > span').last();
    setTimeout(() => {
      let tempPage = {
        id: Utils.generateUUID(), // Only add for histroy in sidemenu
        name: element[0]?.innerText || pagetitle,
        pin_id: null,
        data: null,
        extra: {},
        link: url,
        param: null,
      };
      this.lmService.pinPage = Object.assign({}, tempPage);
      this.createAppHistory();
    }, 1000);
  }
  toggleLoader(status: 'hide' | 'show' = 'show'): void {
    const loader = this.checkPlatformService.isBrowser() ? document.getElementById('loader-main') : null;
    if (loader && status === 'show') {
      loader.style.display = 'flex';
    } else if (loader && status === 'hide') {
      loader.style.display = 'none';
    }
  }

  createAppHistory() {
    // Runs on every navigation. Wrapped so a malformed/legacy stored value can
    // never throw and break routing (the cause of the settings-route freeze).
    try {
      let routerHistory: any = Crypto.decrypt(this.localStorageService?.GetItem('router-history'));

      // Crypto.decrypt already returns the parsed value. Only a legacy plain
      // string needs parsing; never JSON.parse an already-parsed object/array
      // (that throws `"[object Object]" is not valid JSON`).
      if (typeof routerHistory === 'string') {
        try {
          routerHistory = JSON.parse(routerHistory);
        } catch {
          routerHistory = null;
        }
      }

      // Anything that isn't a usable array → start fresh.
      if (!Array.isArray(routerHistory)) {
        routerHistory = [];
      }

      // Drop any existing entry with the same name, then append current page.
      routerHistory = routerHistory.filter(item => item?.name !== this.lmService.pinPage?.name);
      routerHistory.push({ ...this.lmService.pinPage, title: this.lmService.pinPage?.name });

      if (routerHistory.length > 10) {
        routerHistory.shift();
      }

      this.localStorageService.SetItem('router-history', Crypto.encrypt(routerHistory));
    } catch (e) {
      // Corrupt history must never block navigation — reset and continue.
      console.error('createAppHistory failed, resetting router-history:', e);
      this.localStorageService.RemoveItem('router-history');
    }
  }
}
